import type { ReactNode } from "react";
import type { AppRouter } from "@laborup/nova-client";
import type { inferRouterOutputs } from "@trpc/server";
import type { LucideIcon } from "lucide-react";

import type { CandidacySource, CandidacyStatus } from "@/types";

// The nova profile the sheet renders from, used as-is. No remapping layer.
export type UserSheetProfile =
  inferRouterOutputs<AppRouter>["internal"]["user"]["active"]["getCandidacySheetData"];

// Which sub-view of the comms rail to show. "email"/"messages" live in the
// Candidate scope, "comments" in the Internal one, so a tab is only meaningful
// alongside the scope that hosts it.
export type UserSheetCommsTab = "comments" | "email" | "messages";

// Which rail of the comms panel is showing. Lives here rather than in the panel
// so callers that request a scope (citations, header actions) can name one
// without importing the panel.
export enum CommsScope {
  CANDIDATE = "CANDIDATE",
  COMPANY = "COMPANY",
  INTERNAL = "INTERNAL",
}

// Candidacy fields the comms components need, assembled by the host from
// candidacy-info (candidacy scalars) plus the nova profile (name, phone).
export interface UserSheetCommsCandidacy {
  firstName: string | null;
  id: string;
  lastName: string | null;
  phone: string | null;
  stafferAgentActive: boolean;
  status: CandidacyStatus;
  talentId: number | null;
}

// Raw values behind the header's metric row. Consumers pass these (search
// hydration vs candidacy info); the header owns labels, icons, and formatting.
// The loose ping shape accepts both nova's and guac's LastPing variants.
export interface UserSheetMetricValues {
  commuteMinutes?: number | null;
  lastAiCommunication?: { at?: string; stafferId?: number } | null;
  lastHumanCommunication?: { at?: string; stafferId?: number } | null;
}

export type UserSheetMetric =
  | {
      chip?: string | null;
      Icon: LucideIcon;
      isLoading?: boolean;
      key: string;
      kind: "value";
      label: string;
      priority?: "primary" | "secondary";
      value: string;
    }
  // Rendered as the candidate stated it, with a trend against `comparePay`
  // (e.g. the job's offered rate) only when the two share a unit.
  | {
      comparePay: number | null;
      key: string;
      kind: "pay";
      label: string;
      pay: number | null;
      priority?: "primary" | "secondary";
    };

export interface UserSheetTab {
  content: ReactNode;
  count?: number;
  indicator?: ReactNode;
  isMoreTab?: boolean;
  label: string;
  value: string;
}

export interface UserSheetPipeline {
  candidacyId: string;
  company: string;
  companyId: string;
  jobId: string;
  status: CandidacyStatus;
  title: string;
}

export interface UserSheetSubmission {
  candidacyId: string;
  company: string;
  companyId: string;
  jobId: string;
  postSubmissionReason?: string | null;
  postSubmissionStatus?: CandidacyStatus | null;
  source?: CandidacySource | null;
  submittedAt: string;
  title: string;
}
